import React, { FC, useEffect, useMemo, useRef } from 'react'
import { IDatahubTask, ITaskAITextDetailsForm } from '@libs/api/build/tasks/models'
import { Autolocalize, FormikInput, FormikTextArea, Grid, UserInteraction } from '@libs/components'

import Skeleton from './Skeleton'
import { Formik } from 'formik'
import { Form, FormItem, Radio, Select, Checkbox } from 'formik-antd'
import Styled from './Styled'
import { useLocalization } from '@libs/localization'
import { createValidationSchema } from '@libs/util'
import { useCaseById, useContactPersonsByCaseId } from 'modules/cases/hooks'
import { useBranchUsersList } from 'modules/auth/ActiveDirectoryPageContent/hooks'
import { useContactPersons, usePropertyTypes } from 'modules/orders/hooks'
import { Help24 } from '@carbon/icons-react'
import { useScreenMatch } from '@libs/theme'

const { Row, Col } = Grid

interface IFormText {
	inputArea: {
		heading: string
		name: string
		description: string
	}
	radioGroup: {
		heading: string
		name: string
		description: string
		options: string[]
	}
	propertyTypeDesc: string
	questionnaireDesc: string
	responsibleAgentDesc: string
}

interface ATTextDetailsFormProps {
	task: IDatahubTask
	setIsLoading: (isLoading: boolean) => void
	onCompleteClick: (formData: ITaskAITextDetailsForm) => void
	onQuestionnaireChange: (isQuestionnaireSentByEmail: boolean) => void
	initialValues?: ITaskAITextDetailsForm | null
	stepKey?: string | number
	isSendEmailAvailable?: boolean
}

export default function ATTextDetailsForm({
	task,
	setIsLoading,
	onCompleteClick,
	onQuestionnaireChange,
	initialValues,
	stepKey,
	isSendEmailAvailable,
}: ATTextDetailsFormProps) {
	const { t } = useLocalization()
	const { data: contactPersons = [] } = useContactPersonsByCaseId(task.caseId)
	const isInitializingRef = useRef(false)

	const text: IFormText = {
		inputArea: {
			heading: t('modals.aiTextDetails.inputArea.title'),
			name: 'textForCopyWriter',
			description: t('modals.aiTextDetails.inputArea.description'),
		},
		radioGroup: {
			heading: t('modals.aiTextDetails.options.title'),
			name: 'offerForm',
			description: t('modals.aiTextDetails.options.description'),
			options: [t('common.terms.descriptive'), t('common.terms.lightlyColorful'), t('common.terms.colorful')],
		},
		propertyTypeDesc: t('modals.aiTextDetails.propertyTypeLabel'),
		questionnaireDesc: t('modals.aiTextDetails.questionnaireInstructions'),
		responsibleAgentDesc: t('modals.aiTextDetails.responsibleAgentInstructions'),
	}

	const {
		data: propertyTypeKeys = [],
		isLoading: isPropertyTypeListLoading,
		isFetched: isPropertyTypeListFetched,
	} = usePropertyTypes()

	const {
		data: userList = [],
		isLoading: isUserListLoading,
		isFetched: isUserListFetched,
	} = useBranchUsersList(task.branchId)

	const propertyTypes = useMemo(
		() =>
			propertyTypeKeys
				.map(({ name }) => {
					// const formattedKey = t(`orders.propertyTypes.${camelCase(key)}`)
					return {
						value: name,
						label: name,
					}
				})
				.sort((prev, next) => (prev.label < next.label ? -1 : 1)),
		[propertyTypeKeys]
	)

	const { data: caseDetails } = useCaseById(task.caseId)

	const propertyTypeLabel = propertyTypes.find(candidate => candidate?.value === caseDetails?.property?.type)?.label

	const isLoading = isPropertyTypeListLoading || isUserListLoading
	const isFetched = isPropertyTypeListFetched && isUserListFetched

	useEffect(() => {
		setIsLoading(isLoading)
	}, [isLoading, setIsLoading])

	useEffect(() => {
		// Set flag when component mounts or stepKey changes
		isInitializingRef.current = true
		const timer = setTimeout(() => {
			isInitializingRef.current = false
		}, 100)
		return () => clearTimeout(timer)
	}, [stepKey])

	const combinedInitialValues: ITaskAITextDetailsForm = useMemo(
		() =>
			initialValues ?? {
				inputArea: '',
				propertyType: propertyTypeLabel || '',
				isQuestionnaireRequired: false,
				responsibleAgent: userList?.find(({ id }) => id === task.contactPersonId)?.fullName || '',
				contactType: contactPersons?.[0]?.type || '',
				email: contactPersons?.[0]?.email || '',
				contactName: contactPersons?.[0]?.name || '',
				radioGroup: text.radioGroup.options[0],
			},
		[userList, task, text.radioGroup.options, propertyTypeLabel, contactPersons, initialValues]
	)

	const match = useScreenMatch()

	const formSchema = useMemo(
		() =>
			createValidationSchema(yup =>
				yup.object().shape({
					inputArea: yup.string().required(),
					propertyType: yup.string().required(),
					isQuestionnaireRequired: yup.boolean().required(),
					responsibleAgent: yup.string().required(),
					contactType: yup.string().when('isQuestionnaireRequired', {
						is: true,
						then: yup.string().required(),
					}),
					email: yup.string().when('isQuestionnaireRequired', {
						is: true,
						then: yup.string().email().required(),
					}),
					contactName: yup.string().when('isQuestionnaireRequired', {
						is: true,
						then: yup.string().required(),
					}),
				})
			),
		[]
	)

	const generateTooltip = (desc: string) => {
		const defaultSize = {
			title: desc,
			icon: <Help24 />,
			placement: 'rightTop',
		}

		if (desc.length > 200) {
			return {
				...defaultSize,
				overlayStyle: { maxWidth: 400 },
			}
		} else {
			return defaultSize
		}
	}

	const QuestionnaireSection: FC = () => {
		const { contactPersonTypeOptions, isLoading: isContactTypeListLoading } = useContactPersons()

		return (
			<>
				<FormItem name="contactType" label={t('common.terms.contactPerson')} required>
					<Select name="contactType" disabled={isContactTypeListLoading}>
						{!isContactTypeListLoading &&
							contactPersonTypeOptions &&
							contactPersonTypeOptions.map(({ value, label }) => (
								<Select.Option key={value} value={value}>
									{label}
								</Select.Option>
							))}
					</Select>
				</FormItem>
				<FormItem name="contactName" label={t('common.terms.name')} required>
					<FormikInput name="contactName" />
				</FormItem>
				<FormItem name="email" label={t('common.terms.email')} required>
					<FormikInput name="email" />
				</FormItem>
			</>
		)
	}

	return (
		<Formik<ITaskAITextDetailsForm>
			key={stepKey}
			initialValues={combinedInitialValues}
			validationSchema={formSchema}
			onSubmit={values => {
				// Prevent auto-submit during initialization
				if (isInitializingRef.current) {
					return
				}
				onCompleteClick(values)
			}}
		>
			{({ values, isSubmitting: isSubmittingForm }) => {
				onQuestionnaireChange(values.isQuestionnaireRequired)

				return (
					<UserInteraction enabled={!isSubmittingForm}>
						<Form id="task-ai-text-details-form" layout="vertical">
							<Autolocalize />
							<Row>
								<Col span={24}>
									{!isFetched ? (
										<Skeleton.AITextDetailsForm />
									) : (
										<Styled.FormWrapper>
											<FormItem
												name="inputArea"
												label={<>{text.inputArea.heading}</>}
												required
												tooltip={generateTooltip(text.inputArea.description)}
											>
												<FormikTextArea
													name="inputArea"
													autoSize={{ minRows: 2, maxRows: 4 }}
													size="small"
													placeholder={t('modals.aiTextDetails.inputArea.placeholder')}
												/>
											</FormItem>

											<FormItem
												name="propertyType"
												label={<>{t('common.terms.propertyType')}</>}
												required
												tooltip={generateTooltip(text.propertyTypeDesc)}
											>
												<Select
													showSearch
													name="propertyType"
													placeholder={t('modals.aiTextDetails.propertyTypeLabel')}
													disabled={isPropertyTypeListLoading}
												>
													{propertyTypes.map(({ value, label }, index) => (
														<Select.Option key={index} value={value}>
															{label}
														</Select.Option>
													))}
												</Select>
											</FormItem>

											<FormItem
												name="radioGroup"
												label={<>{text.radioGroup.heading}</>}
												required
												tooltip={generateTooltip(text.radioGroup.description)}
											>
												{match.map({
													xxs: (
														<Select name={'radioGroup'}>
															{text.radioGroup.options.map(option => (
																<Select.Option key={option} name={option} value={option}>
																	{option}
																</Select.Option>
															))}
														</Select>
													),
													md: (
														<Radio.Group name="radioGroup">
															{text.radioGroup.options.map(option => (
																<Radio key={option} name={option} value={option} size="small">
																	{option}
																</Radio>
															))}
														</Radio.Group>
													),
												})}
											</FormItem>

											{isSendEmailAvailable && (
												<FormItem
													name="isQuestionnaireRequired"
													label={<>{t('common.terms.questionnaire')}</>}
													tooltip={generateTooltip(text.questionnaireDesc)}
												>
													<Checkbox name="isQuestionnaireRequired">
														{t('modals.aiTextDetails.conductQuestionnaireAction')}
													</Checkbox>
												</FormItem>
											)}

											{values.isQuestionnaireRequired && <QuestionnaireSection />}

											<FormItem
												name="responsibleAgent"
												label={<>{t('common.terms.responsibleAgent')}</>}
												required
												tooltip={generateTooltip(text.responsibleAgentDesc)}
											>
												<Select
													name="responsibleAgent"
													placeholder={t('common.placeholders.selectAgent')}
													disabled={isUserListLoading}
												>
													{!isUserListLoading &&
														userList &&
														userList.map(({ fullName, email }) => (
															<Select.Option key={fullName} value={fullName}>
																{fullName} ({email})
															</Select.Option>
														))}
												</Select>
											</FormItem>
										</Styled.FormWrapper>
									)}
								</Col>
							</Row>
						</Form>
					</UserInteraction>
				)
			}}
		</Formik>
	)
}
